import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // 2FA gate — backend issues a short-lived challenge instead of a session token.
      if (data.challenge === "totp_required") {
        return {
          ok: false,
          challenge: "totp_required",
          challenge_token: data.challenge_token,
          user_email: data.user_email,
          user_role: data.user_role,
          recovery_codes_remaining: data.recovery_codes_remaining,
        };
      }
      if (data.token) localStorage.setItem("rf_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("rf_token");
    setUser(false);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch { return null; }
  };

  const setSession = async (token, userLike) => {
    // Used by the one-click Demo Mode launcher — persists the token and
    // hydrates the user object without going through /auth/login.
    if (token) localStorage.setItem("rf_token", token);
    // Refresh from /auth/me so we get the enriched (is_demo, 2FA, etc.) shape.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      setUser(userLike || null);
      return userLike || null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
